import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Code, Brain, Cloud, Database, Shield, Lock, Bot, Sparkles, Eye, Mic, TrendingUp, Scale, GraduationCap, Blocks, Utensils } from 'lucide-react';
import { ServiceCard } from '../components/ServiceCard';
import { Service } from '../types';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const ServicesPage: React.FC = () => {
  useSEO('services');
  const { isBannerVisible, bannerHeight } = useBanner();
  
  const services: Service[] = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Cognitive Reach",
      subtitle: "Digital Marketing",
      description:
        "AI-powered marketing solutions that amplify your brand's reach and engagement across digital channels with intelligent automation and data-driven insights.",
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Cognitive Train",
      subtitle: "AI Training & Consulting",
      description:
        "Comprehensive AI education, custom model training, strategy consulting, and change management for successful AI adoption.",
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Cognitive Cloud",
      subtitle: "Cloud Infrastructure",
      description:
        "Scalable, secure cloud solutions that grow with your business and ensure 99.9% uptime with enterprise-grade security and performance.",
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Cognitive Build",
      subtitle: "Custom Software",
      description:
        "Tailored software solutions built with cutting-edge technologies to solve your unique business challenges and drive operational efficiency.",
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Cognitive Intelligence",
      subtitle: "AI & Data Tools",
      description:
        "Advanced AI and data analytics tools that transform raw data into actionable business insights, enabling smarter decision-making.",
    },
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Cognitive Agents",
      subtitle: "AI Automation & RPA",
      description:
        "Intelligent automation solutions with AI agents, robotic process automation, and workflow optimization that boost productivity by up to 50%.",
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Cognitive Generate",
      subtitle: "Generative AI Solutions",
      description:
        "Custom generative AI implementations including content creation, code generation, and multimodal AI solutions for enhanced creativity and efficiency.",
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Cognitive Vision",
      subtitle: "Computer Vision & AI Analytics",
      description:
        "Advanced computer vision solutions for image and video analytics, quality control automation, and intelligent visual data processing.",
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Cognitive Voice",
      subtitle: "Conversational AI & Voice",
      description:
        "AI-powered voice solutions including chatbots, virtual assistants, speech analytics, and interactive voice response systems.",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Cognitive Predict",
      subtitle: "Advanced Analytics & ML",
      description:
        "Machine learning-powered predictive analytics for demand forecasting, risk assessment, and data-driven decision making.",
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Cognitive IoT",
      subtitle: "Internet of Things",
      description:
        "Connected device solutions that bridge the physical and digital worlds for smarter operations and enhanced business intelligence.",
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Cognitive Secure",
      subtitle: "Cybersecurity",
      description:
        "Advanced cybersecurity solutions powered by AI to protect your business from evolving cyber threats with comprehensive threat detection and response.",
    },
    {
      icon: <Utensils className="w-8 h-8" />,
      title: "Cognitive Robotics",
      subtitle: "Robotics Implementation",
      description:
        "Complete robotics automation consulting and implementation services. Expert guidance for vendor selection, system integration, deployment, and ongoing support.",
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Cognitive Compliance",
      subtitle: "Regulatory & Legal AI",
      description:
        "Specialized AI solutions for regulatory compliance, legal document analysis, audit automation, and governance frameworks.",
    },
    {
      icon: <Blocks className="w-8 h-8" />,
      title: "Cognitive Ledger",
      subtitle: "Blockchain & Distributed Ledger",
      description:
        "Secure blockchain solutions including smart contracts, DApps, supply chain transparency, and decentralized identity management.",
    },
  ];

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 py-4 px-2 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent leading-loose overflow-visible min-h-fit"
            style={{ lineHeight: '1.75', minHeight: 'auto', overflow: 'visible' }}
          >
            Our Services
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-7xl mx-auto leading-relaxed">
            Comprehensive AI-powered digital solutions designed to transform your 
            business operations and drive sustainable growth in the digital age.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Why Choose Our Services?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">24/7</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Support</div>
                <p className="text-gray-700 dark:text-gray-300">
                  Round-the-clock technical support and monitoring to ensure your 
                  systems run smoothly.
                </p>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Enterprise</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Reliability</div>
                <p className="text-gray-700 dark:text-gray-300">
                  Enterprise-grade infrastructure designed for high availability and 
                  optimal performance.
                </p>
              </div>
              
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Expert</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Team</div>
                <p className="text-gray-700 dark:text-gray-300">
                  Experienced professionals committed to delivering exceptional results 
                  that drive business value.
                </p>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 text-lg mb-4">
              Ready to transform your business with AI-powered solutions? 
              Let's discuss how our services can help you achieve your goals.
            </p>
            <div className="text-center mb-8">
              <Link
                to="/about"
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                Learn more about our Cognitive Blueprint™ methodology →
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Get Started Today
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Consultation
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};